/**
 * DimensionIAS Contact & Enquiry Service
 * Handles client-side validation, anti-spam honeypot verification,
 * and POST requests to the WordPress REST API endpoint (/wp-json/dimensionias/v1/contact)
 * with graceful fallback to /send-enquiry.php.
 *
 * Env vars (defined in .env):
 *   REACT_APP_WP_API_URL  — Primary WordPress REST endpoint
 */

const WP_REST_ENDPOINT =
  process.env.REACT_APP_WP_API_URL ||
  'https://dimensionias.com/cms/wp-json/dimensionias/v1/contact';

/**
 * PHP fallback endpoint.
 * Set REACT_APP_PHP_FALLBACK_URL in .env to override.
 * Defaults to the relative path which only works when deployed — NOT in local dev
 * (the Node.js dev server cannot execute PHP files).
 */
const PHP_FALLBACK_ENDPOINT =
  process.env.REACT_APP_PHP_FALLBACK_URL || '/send-enquiry.php';

/** True when running locally (localhost / 127.0.0.1) */
const IS_LOCAL_DEV =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// ---------------------------------------------------------------------------
// Client-Side Validation
// ---------------------------------------------------------------------------

/**
 * Validate contact/enquiry form fields on the client side.
 * @param {Object} formData
 * @returns {{ isValid: boolean, errors: Object, isBot?: boolean }}
 */
export const validateContactForm = (formData) => {
  const errors = {};

  // Honeypot anti-spam check
  if (formData.website) {
    return { isValid: false, errors: { bot: 'Spam detected.' }, isBot: true };
  }

  // Name validation
  if (!formData.name || !formData.name.trim()) {
    errors.name = 'Full Name is required.';
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.email || !formData.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!emailRegex.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  // Phone validation
  const phoneDigits = formData.phone ? formData.phone.replace(/\D/g, '') : '';
  if (!formData.phone || !formData.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (phoneDigits.length < 10) {
    errors.phone = 'Please enter a valid 10-digit phone number.';
  }

  // Message validation (optional for counselling — callers pass a synthetic message)
  if (formData.message !== undefined && !formData.message.trim()) {
    errors.message = 'Message is required.';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// ---------------------------------------------------------------------------
// Core Submit Logic (shared by Contact & Counselling forms)
// ---------------------------------------------------------------------------

/**
 * Submit an enquiry to the WordPress REST API with PHP fallback.
 *
 * @param {Object} formData  Must contain: name, email, phone, course, message, website
 * @returns {Promise<{ success: boolean, message: string, errors?: Object }>}
 */
export const submitEnquiry = async (formData) => {
  // 1. Client-Side Validation
  const validation = validateContactForm(formData);

  if (validation.isBot) {
    // Return fake success for bots — do not reveal detection
    return { success: true, message: 'Your enquiry has been submitted successfully.' };
  }

  if (!validation.isValid) {
    return {
      success: false,
      message: 'Please fix the errors in the form before submitting.',
      errors: validation.errors
    };
  }

  // 2. Sanitise payload
  const payload = {
    name:    formData.name.trim(),
    email:   formData.email.trim(),
    phone:   formData.phone.trim(),
    course:  formData.course ? formData.course.trim() : 'General Enquiry',
    message: formData.message ? formData.message.trim() : '',
    timestamp: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
  };

  // 3. Primary endpoint — WordPress Custom REST API
  try {
    const response = await fetch(WP_REST_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || 'Your enquiry has been submitted successfully.'
      };
    }

    if (response.status === 429) {
      return {
        success: false,
        message: data.message || 'Too many requests. Please wait a few minutes and try again.'
      };
    }

    if (response.status === 400 && data.errors) {
      return {
        success: false,
        message: data.message || 'Validation failed.',
        errors: data.errors
      };
    }
  } catch (wpError) {
    console.warn(
      'WordPress REST API endpoint unreachable, attempting fallback...',
      wpError
    );
  }

  // 4. Fallback endpoint — PHP mail script
  //    Skip when running locally: the CRA dev server cannot execute PHP, so the
  //    fetch would succeed with a 200 but return PHP source code, causing a JSON
  //    parse error and a misleading catch-block error message.
  if (IS_LOCAL_DEV) {
    console.warn(
      '[contactService] Running on localhost — PHP fallback skipped.\n' +
      'Deploy the WordPress plugin and test on the live site, OR ensure ' +
      'the WP REST API CORS headers are updated and reload.'
    );
    return {
      success: false,
      message:
        'Could not reach the server from localhost. Please deploy the WordPress plugin ' +
        'with updated CORS headers and test on https://dimensionias.com, or contact us ' +
        'directly at +91\u00a07530807530.'
    };
  }

  try {
    const fallbackResponse = await fetch(PHP_FALLBACK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const fallbackData = await fallbackResponse.json();
    return {
      success: fallbackResponse.ok && fallbackData.success,
      message: fallbackData.message || 'Your enquiry has been received successfully.'
    };
  } catch (fallbackError) {
    console.error('All form submission endpoints failed:', fallbackError);
    return {
      success: false,
      message:
        'Failed to submit form. Please check your network connection or contact us directly at +91 7530807530.'
    };
  }
};

// ---------------------------------------------------------------------------
// Counselling / Mentorship Form Adapter
// ---------------------------------------------------------------------------

/**
 * Submit a mentorship/counselling request.
 * Maps CounsellingPage field names to the shared WP endpoint schema
 * and synthesises a message field from the selected reason.
 *
 * @param {{ fullName: string, email: string, phone: string, reason: string }} counsellingData
 * @returns {Promise<{ success: boolean, message: string, errors?: Object }>}
 */
export const submitCounsellingRequest = async (counsellingData) => {
  // Map counselling-specific field names → shared endpoint names
  const mapped = {
    name:    counsellingData.fullName || '',
    email:   counsellingData.email   || '',
    phone:   counsellingData.phone   || '',
    course:  counsellingData.reason  || 'Mentorship / Counselling',
    message: counsellingData.reason
      ? `Mentorship request reason: ${counsellingData.reason}`
      : 'Mentorship session request submitted via the Counselling page.',
    website: '' // Honeypot — always empty for real users
  };

  // Remap any server-side field errors back to counselling field names
  const result = await submitEnquiry(mapped);

  if (!result.success && result.errors) {
    const remappedErrors = {};
    if (result.errors.name)    remappedErrors.fullName = result.errors.name;
    if (result.errors.email)   remappedErrors.email    = result.errors.email;
    if (result.errors.phone)   remappedErrors.phone    = result.errors.phone;
    if (result.errors.message) remappedErrors.reason   = result.errors.message;
    return { ...result, errors: remappedErrors };
  }

  return result;
};

// ---------------------------------------------------------------------------
// Course Registration Adapter
// ---------------------------------------------------------------------------

/**
 * Submit a course registration request from the Courses page popup form.
 * Maps Courses.js field names to the shared WP endpoint schema:
 *   contact → phone
 *   city    → appended to message
 *
 * @param {{ name: string, contact: string, email: string, city: string, course: string }} registrationData
 * @returns {Promise<{ success: boolean, message: string, errors?: Object }>}
 */
export const submitCourseRegistration = async (registrationData) => {
  const cityNote = registrationData.city ? ` | City: ${registrationData.city}` : '';

  const mapped = {
    name:    registrationData.name    || '',
    email:   registrationData.email   || '',
    phone:   registrationData.contact || '',
    course:  registrationData.course  || 'Course Registration',
    message: `Course registration request for: ${registrationData.course || 'N/A'}${cityNote}`,
    website: '' // Honeypot — always empty for real users
  };

  const result = await submitEnquiry(mapped);

  // Remap server-side field errors back to Courses form field names
  if (!result.success && result.errors) {
    const remappedErrors = {};
    if (result.errors.name)  remappedErrors.name    = result.errors.name;
    if (result.errors.email) remappedErrors.email   = result.errors.email;
    if (result.errors.phone) remappedErrors.contact = result.errors.phone;
    return { ...result, errors: remappedErrors };
  }

  return result;
};
